import { cn } from "@tcg/ui";
import { CheckCircle2, Plug, ScanLine, Tag, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthState, usePos } from "@/app/context";
import { useScanInput } from "@/features/sale/hooks/useScanInput";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import { isPrinterError } from "@/lib/printer";
import type { LabelConnection, LabelPrinterSettings as Settings } from "@/lib/printer/labels";

import { LabelPrintModal, type LabelSource } from "./LabelPrintModal";

const CONNECTIONS: LabelConnection[] = ["webusb", "bluetooth", "network", "pdf", "mock", "none"];

/**
 * ラベルプリンター (T07-34): how the label printer is connected (USB TSPL / ZPL, Bluetooth, print
 * bridge, or PDF through the browser for an ordinary printer), the label size (40 × 30 default /
 * 58 × 40), 「プリンターを選ぶ」, 「テスト印字」 (a shelf label made by pos-service), and 「ラベルを作る」:
 * scan a card → its price label again (a lost / damaged sleeve label).
 */
export function LabelPrinterSettings() {
  const { t } = useTranslation();
  const { labels, api } = usePos();
  const unlocked = useAuthState().kind === "unlocked";
  const [draft, setDraft] = useState<Settings>(labels.config);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [source, setSource] = useState<LabelSource | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const set = (changes: Partial<Settings>) => setDraft((d) => ({ ...d, ...changes }));

  const save = async () => {
    await labels.save({ ...draft, usbDevice: labels.config.usbDevice ?? null });
    setStatus({ ok: true, text: t("settings.saved") });
  };

  const choose = async () => {
    await labels.save({ ...draft, usbDevice: null });
    try {
      const result = await labels.driver().connect();
      setStatus(
        result.connected
          ? {
              ok: true,
              text: `${t("settings.status_ok")}${result.name ? ` — ${result.name}` : ""}`,
            }
          : { ok: false, text: t(`print.${result.error ?? "not_found"}`) },
      );
    } catch (error) {
      setStatus({
        ok: false,
        text: t(`print.${isPrinterError(error) ? error.code : "not_found"}`),
      });
    }
  };

  const testLabel = async () => {
    await labels.save({ ...draft, usbDevice: labels.config.usbDevice ?? null });
    setTitle(t("labels.test_title"));
    setSource({
      shelves: [
        { title: t("labels.test_label"), subtitle: t("labels.test_subtitle"), code: "TEST" },
      ],
    });
  };

  const scanInput = useScanInput({
    onScan: (code) => {
      void (async () => {
        try {
          const hit = await call(() => api.GET("/v1/pos/lookup", { params: { query: { code } } }));
          setTitle(
            t("labels.for_card", { title: hit.variant.title.ja ?? hit.variant.title.en ?? code }),
          );
          setSource(
            hit.unit?.id ? { unitCodes: [hit.unit.sku ?? code] } : { variantIds: [hit.variant.id] },
          );
          setStatus(null);
        } catch (error) {
          setStatus({ ok: false, text: errorText(error, t) });
        }
      })();
    },
    onSearch: () => undefined,
    global: false,
    enabled: unlocked,
  });

  return (
    <section aria-labelledby="label-title" className="flex flex-col gap-4">
      <h2 id="label-title" className="text-2xl font-semibold">
        {t("labels.printer_title")}
      </h2>
      <fieldset className="flex flex-wrap gap-2">
        <legend className="mb-1 font-medium">{t("settings.connection")}</legend>
        {CONNECTIONS.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={draft.connection === c}
            onClick={() => set({ connection: c })}
            className={cn(
              "min-h-pos-touch rounded-md border px-5 text-lg",
              draft.connection === c && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {t(`labels.connection_${c}`)}
          </button>
        ))}
      </fieldset>
      {draft.connection === "webusb" ||
      draft.connection === "bluetooth" ||
      draft.connection === "network" ? (
        <fieldset className="flex flex-wrap gap-2">
          <legend className="mb-1 font-medium">{t("labels.language")}</legend>
          {(["tspl", "zpl"] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              aria-pressed={draft.language === lang}
              onClick={() => set({ language: lang })}
              className={cn(
                "min-h-pos-touch rounded-md border px-5 text-lg",
                draft.language === lang && "border-primary bg-primary text-primary-foreground",
              )}
            >
              {t(`labels.language_${lang}`)}
            </button>
          ))}
        </fieldset>
      ) : null}
      <fieldset className="flex flex-wrap gap-2">
        <legend className="mb-1 font-medium">{t("labels.size")}</legend>
        {(["40x30", "58x40"] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={draft.size === value}
            onClick={() => set({ size: value })}
            className={cn(
              "min-h-pos-touch rounded-md border px-6 text-lg",
              draft.size === value && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {value.replace("x", " × ")} mm
          </button>
        ))}
      </fieldset>
      {draft.connection === "network" ? (
        <div className="grid gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("settings.bridge_url")}</span>
            <input
              value={draft.bridgeUrl ?? ""}
              onChange={(e) => set({ bridgeUrl: e.target.value || null })}
              placeholder="https://print-bridge.local:9443"
              className="min-h-pos-touch rounded-md border px-3"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("settings.bridge_token")}</span>
            <input
              type="password"
              value={draft.bridgeToken ?? ""}
              onChange={(e) => set({ bridgeToken: e.target.value || null })}
              autoComplete="off"
              className="min-h-pos-touch rounded-md border px-3"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("settings.bridge_printer")}</span>
            <input
              value={draft.bridgePrinter ?? ""}
              onChange={(e) => set({ bridgePrinter: e.target.value || null })}
              placeholder="labels"
              className="min-h-pos-touch rounded-md border px-3"
            />
          </label>
        </div>
      ) : null}
      <p className="rounded-md bg-muted p-3">{t(`labels.hint_${draft.connection}`)}</p>
      <div className="flex flex-wrap gap-2">
        {draft.connection === "webusb" || draft.connection === "bluetooth" ? (
          <button
            type="button"
            onClick={() => void choose()}
            className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg"
          >
            <Plug aria-hidden className="size-5" />
            {t("settings.choose_printer")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void testLabel()}
          disabled={draft.connection === "none"}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg disabled:opacity-50"
        >
          <Tag aria-hidden className="size-5" />
          {t("labels.test")}
        </button>
        <button
          type="button"
          onClick={() => void save()}
          className="min-h-pos-touch rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground"
        >
          {t("settings.save")}
        </button>
      </div>
      <label className="flex min-h-pos-touch max-w-xl items-center gap-2 rounded-md border px-3 focus-within:ring-2 focus-within:ring-ring">
        <ScanLine aria-hidden className="size-6 shrink-0" />
        <span className="sr-only">{t("labels.scan_label")}</span>
        <input
          {...scanInput.inputProps}
          type="search"
          autoComplete="off"
          placeholder={t("labels.scan_placeholder")}
          className="min-w-0 flex-1 bg-transparent text-lg outline-none"
        />
      </label>
      {status ? (
        <p
          role="status"
          className={cn(
            "flex items-center gap-2 text-lg",
            status.ok ? "text-pos-ok" : "text-pos-problem",
          )}
        >
          {status.ok ? (
            <CheckCircle2 aria-hidden className="size-6" />
          ) : (
            <TriangleAlert aria-hidden className="size-6" />
          )}
          {status.text}
        </p>
      ) : null}
      <LabelPrintModal
        open={source !== null}
        onClose={() => setSource(null)}
        source={source}
        {...(title ? { title } : {})}
      />
    </section>
  );
}
